import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as yup from "yup";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeaderTitleStyles,
} from "../Common/styles/modal";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { ClearOutlined, CloseOutlined } from "@mui/icons-material";
import { DrawerForm } from "../Common/styles/drawer";
import { AxiosResponse } from "axios";

let schema = yup.object().shape({
  name: yup.string().required("Form Name is Required").trim(),
  description: yup.string().optional(),
});

const AddPhaseFormModal = ({
  showModal,
  closeModal,
  data,
  phaseId,
  studyId,
  refreshPage,
  formId,
  showPackageSelection,
}: any) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [surveyPackageData, setSurveyPackageData] = useState<any>([]);
  const [isLoading, setLoading] = useState(true);

  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        ...values,
        surveyPackageId: values.surveyPackageId || null,
      };
      if (formId) {
        await http.patch(`/study/${studyId}/forms/${formId}`, body);
      } else {
        await http.post(
          `/study/${studyId}/study-phase/${phaseId}/phase_form`,
          body
        );
      }
      closeModal();
      toastMessage(
        "success",
        formId ? "Form updated successfully" : "Form created successfully"
      );
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  useEffect(() => {
    const fetchSurveyPackageData = async () => {
      try {
        setLoading(true);
        const res: AxiosResponse = await http.get(
          `/study/${studyId}/survey-package`
        );
        const data = res.data?.data;

        const formattedData = data?.map((item: any) => ({
          surveyPackageId: item?.id,
          name: item?.name || "",
        }));

        setSurveyPackageData(formattedData || []);
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    if (showPackageSelection) {
      fetchSurveyPackageData();
    } else {
      setLoading(false);
    }
  }, [studyId, showPackageSelection]);

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Typography
            sx={{
              ...ModalHeaderTitleStyles,
              flex: 1,
              minWidth: "0px",
            }}
            title={data?.id ? "Edit Form" : `Add Form to ${data?.phaseName}`}
            noWrap
          >
            {data?.id ? "Edit Form" : `Add Form to ${data?.phaseName}`}
          </Typography>

          <IconButton onClick={closeModal}>
            <CloseOutlined />
          </IconButton>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={25} />
          </Box>
        ) : (
          <Formik
            initialValues={{
              name: data?.name || "",
              description: data?.description || "",
              surveyPackageId: data?.surveyPackageId || "",
            }}
            validationSchema={schema}
            onSubmit={(values) => {
              submitHandler(values);
            }}
          >
            {({ getFieldProps, errors, touched, values, setFieldValue }) => (
              <Form style={DrawerForm}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="form-name">
                    Form Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Form name"
                    id="form-name"
                    {...getFieldProps("name")}
                    error={touched?.name && errors?.name ? true : false}
                    helperText={
                      touched?.name && errors?.name
                        ? (errors?.name as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="description">
                    Form Description
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    placeholder="Form description"
                    id="description"
                    {...getFieldProps("description")}
                    error={
                      touched?.description && errors?.description ? true : false
                    }
                    helperText={
                      touched?.description && errors?.description
                        ? (errors?.description as string)
                        : " "
                    }
                  />
                </FormControl>
                {showPackageSelection && (
                  <FormControl sx={InputWrapper}>
                    <FormLabel sx={LabelStyle} htmlFor="surveyPackage">
                      Send Survey Package on Completion
                    </FormLabel>
                    <Select
                      id="surveyPackage"
                      value={values.surveyPackageId}
                      onChange={(e) => {
                        setFieldValue("surveyPackageId", e.target.value);
                      }}
                      endAdornment={
                        surveyPackageData.length > 0 && (
                          <InputAdornment
                            sx={{ position: "absolute", right: 32 }}
                            position="end"
                          >
                            <IconButton
                              onClick={() => {
                                setFieldValue("surveyPackageId", "");
                              }}
                            >
                              <ClearOutlined />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                    >
                      {surveyPackageData?.length ? (
                        surveyPackageData.map((item: any, index: number) => (
                          <MenuItem
                            key={item.surveyPackageId}
                            value={item.surveyPackageId}
                          >
                            {item.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem key={"None"} value={""} disabled>
                          No Data available
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
                <Box sx={{ mt: "auto" }}>
                  <Divider />
                  <Box sx={ModalDrawerButtonStyles}>
                    {!submitLoader ? (
                      <>
                        <Button onClick={closeModal} variant="outlined">
                          Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                          {formId ? "Save" : "Add"}
                        </Button>
                      </>
                    ) : (
                      <CircularProgress size={25} />
                    )}
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Modal>
  );
};

export default AddPhaseFormModal;
